import { useCallback, useState } from 'react';
import { useEffectOnce } from 'react-use';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { TableCheckItems } from 'CorePackage/MainModule/types/TableCheckItems';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getSortOrder } from 'CorePackage/MainModule/common/getSortOrder';
import { KnmListTable } from 'KnmListPackage/MainModule/components/KnmListTable';
import { IKnmRaw } from 'KnmListPackage/MainModule/interfaces/IKnmRaw';
import { ITableColumn } from 'CorePackage/TableModule/interfaces/ITableColumn';
import { TablePage } from 'CorePackage/TableModule/components/TablePage';
import { TableColumnSortEnum } from 'CorePackage/MainModule/enums/TableColumnSortEnum';
import { getLawInterface } from 'CorePackage/MainModule/reducers/coreReducer';
import { LawInterfacesEnum } from 'CorePackage/MainModule/enums/LawInterfacesEnum';

import { initialPreventionTableColumns } from '../constants/initialPreventionTableColumns';
import { PreventionPanel } from './PreventionPanel';
import {
  preventionDeleteKnms,
  preventionGetKnms,
  preventionGetKnmsNextPage,
  preventionSetOrder,
  preventionSetSearchValue,
} from '../actions/preventionActions';
import { getPreventionKnms, getPreventionTotalCount } from '../selectors/preventionSelectors';

const federalLawColumn: ITableColumn<IKnmRaw> = {
  id: 'federalLaw',
  name: 'ФЗ',
  sort: TableColumnSortEnum.NONE,
};

interface IPreventionWithoutStore {
  knms: IKnmRaw[];
  totalCount: number;
  isErknm: boolean;
}

const PreventionWithoutStore = ({ knms, totalCount, isErknm }: IPreventionWithoutStore) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [isVisibleModal, setIsVisibleModal] = useState<boolean>(false);
  const [itemsCheck, setItemsCheck] = useState<TableCheckItems>({});

  const [tableColumns, setTableColumns] = useState<ITableColumn<IKnmRaw>[]>(
    isErknm ? initialPreventionTableColumns : [...initialPreventionTableColumns, federalLawColumn],
  );
  const [searchInputValue, setSearchInputValue] = useState<string>('');

  useEffectOnce(() => {
    dispatch(preventionGetKnms());
  });

  const handleDeleteClick = useCallback(() => {
    const ids = Object.keys(itemsCheck)
      .filter(id => itemsCheck[id])
      .map(item => +item);

    dispatch(preventionDeleteKnms(ids));
    setIsVisibleModal(false);
  }, [dispatch, itemsCheck]);

  const handleCloseModal = useCallback(() => {
    setIsVisibleModal(false);
  }, []);

  const handleClickDeleteButton = useCallback(() => {
    setIsVisibleModal(true);
  }, []);

  const handleLoadButton = useCallback(() => {
    dispatch(preventionGetKnmsNextPage());
  }, [dispatch]);

  const onChangeSort = useCallback(
    (newColumnsData: ITableColumn<IKnmRaw>[]) => {
      const newOrder = getSortOrder(newColumnsData);

      dispatch(preventionSetOrder(newOrder));
      dispatch(preventionGetKnms());
    },
    [dispatch],
  );

  const handleSearch = useCallback(() => {
    dispatch(preventionSetSearchValue(searchInputValue));
    dispatch(preventionGetKnms());
  }, [dispatch, searchInputValue]);

  return (
    <TablePage>
      <PreventionPanel
        knms={knms}
        dataCount={totalCount}
        columns={tableColumns}
        itemsCheck={itemsCheck}
        searchInputValue={searchInputValue}
        onDeleteButton={handleClickDeleteButton}
        onSearch={handleSearch}
        setSearchInputValue={setSearchInputValue}
      />
      <KnmListTable
        tableColumns={tableColumns}
        itemsCheck={itemsCheck}
        knms={knms}
        totalCount={totalCount}
        setItemsCheck={setItemsCheck}
        setTableColumns={setTableColumns}
        handleLoadClick={handleLoadButton}
        onChangeSort={onChangeSort}
        isConfirmModalVisible={isVisibleModal}
        handleDeleteClick={handleDeleteClick}
        handleDeleteModalClose={handleCloseModal}
        checkedNames={['ПМ', 'ПМ', 'ПМ']}
      />
    </TablePage>
  );
};

const mapStateToProps = (state: IRootState) => ({
  totalCount: getPreventionTotalCount(state),
  knms: getPreventionKnms(state),
  isErknm: getLawInterface(state) === LawInterfacesEnum.FZ_248,
});

export const Prevention = connect(mapStateToProps)(PreventionWithoutStore);
